'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ManufacturerHintsFragmentDoc } from '@packages/gql/generated/shopping/ManufacturerHintsFragmentDoc';
import { useIntl } from 'react-intl';
import { messages } from './messages';

/* GraphQL */ `
  fragment ManufacturerHints on Product {
    manufacturerHints {
      companyName
      mail
      address {
        city
        countryCode
        houseNumber
        postalCode
        street
      }
    }
  }
`;

type ManufacturerHintsProps = {
  data: FragmentType<typeof ManufacturerHintsFragmentDoc>;
  className?: string;
};

/**
 * <p>
 * The <code>ManufacturerHints</code> component displays the manufacturer hints for a product.
 * They contain:
 *  <ul>
 *    <li>company name/manufacturer</li>
 *    <li>address</li>
 *    <li>email</li>
 *  </ul>
 * </p>
 */
export const ManufacturerHints = ({ data, className }: ManufacturerHintsProps) => {
  const { manufacturerHints } = getFragmentData(ManufacturerHintsFragmentDoc, data);
  const { formatMessage } = useIntl();

  return (
    <aside className={className}>
      <p className="text-size-body2 font-bold">{formatMessage(messages.manufacturerHintsTitle)}:</p>
      <div className="space-y-1.5">
        {manufacturerHints.map((hint) => (
          <address key={hint.companyName} className="text-size-body2 not-italic">
            {/* manufacturer */}
            <p>{formatMessage(messages.manufacturerHintsManufacturer)}:</p>
            <p>{hint.companyName}</p>

            {/* address */}
            <p>{formatMessage(messages.manufacturerHintsAddress)}:</p>
            <p>{`${hint.address.street} ${hint.address.houseNumber}`}</p>
            <p>{`${hint.address.countryCode}-${hint.address.postalCode} ${hint.address.city}`}</p>

            {/* email */}
            <p>{formatMessage(messages.manufacturerHintsEmail)}:</p>
            <p>{hint.mail}</p>
          </address>
        ))}
      </div>
    </aside>
  );
};
